import React, { ReactNode, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Sidebar from './Sidebar';
import type { MenuItem } from './Sidebar';
import ThemeToggle from './ThemeToggle';
import TillStatusPill from './TillStatusPill';
import NotificationBell from './NotificationBell';
import AppSwitcher from './AppSwitcher';
import { useAuth } from '../context/AuthContext';
import { useModule } from '../context/ModuleContext';
import { useAppAccess } from '../context/AppAccessContext';
import { Permissions } from '../constants/Permissions';
import { APPLICATION_NAME, APPLICATION_SHORT_NAME } from '../constants/AppConfig';
// Self-hosted (works offline in the Electron build) — used by the header title.
import '@fontsource/space-grotesk/500.css';
import '@fontsource/space-grotesk/700.css';
import '../styles/Layout.css';
import './CashierLayout.css';
import {
  LayoutDashboard, Building2, Warehouse, Package, Tag, DollarSign,
  Ruler, ArrowLeftRight, ShoppingCart, ShoppingBag, Handshake, Users,
  ClipboardList, ClipboardCheck, Ban, Clock, PackageCheck, ShieldCheck,
  TrendingUp, Layers, Activity, FileBarChart, ScanLine, AlertTriangle,
  Receipt, LockKeyhole, History, User, KeyRound, Settings, CreditCard,
  BarChart2, Coins, Store, CalendarRange, Upload, Download,
  UtensilsCrossed, ChefHat, Coffee, ScrollText, LogOut, FlaskConical,
  Sliders, Barcode, Printer, Car, CalendarDays, LayoutGrid, BookOpen,
  Landmark, Calculator, Wallet, ListTree, TicketPercent, BadgePercent, CalendarCheck,
} from 'lucide-react';
import ProductLog from '../images/logo.png';

interface LayoutProps {
  children: ReactNode;
}

// ── Nav helpers ───────────────────────────────────────────────────────────────

/** Inserts a divider between every non-empty group. */
const section = (id: string, ...groups: MenuItem[][]): MenuItem[] => {
  const result: MenuItem[] = [];
  const populated = groups.filter((g) => g.length > 0);
  populated.forEach((g, idx) => {
    if (idx > 0) {
      result.push({ path: `__div__${id}__${idx}`, label: '', divider: true });
    }
    result.push(...g);
  });
  return result;
};

/** True if the list contains at least one real (non-divider) item. */
const hasItems = (items: MenuItem[]): boolean => items.some((i) => !i.divider);

// Icon shorthand
const icon = (el: React.ReactElement) => el;

// ── Layout component ──────────────────────────────────────────────────────────

const Layout: React.FC<LayoutProps> = ({ children }) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { user, logout, hasPermission, hasAnyPermission, isCashier } = useAuth();
  const { isModule } = useModule();
  const { currentApp, availableApps } = useAppAccess();

  const [sidebarExpanded, setSidebarExpanded] = useState<boolean>(() => {
    const saved = localStorage.getItem('sidebarExpanded');
    return saved !== null ? JSON.parse(saved) : true;
  });

  useEffect(() => {
    localStorage.setItem('sidebarExpanded', JSON.stringify(sidebarExpanded));
  }, [sidebarExpanded]);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  // ──────────────────────────────────────────────────────────────────────────
  // Products
  // ──────────────────────────────────────────────────────────────────────────
  const productsChildren = section('products',
    // Group 1 — product items
    hasPermission(Permissions.Products.View) ? [
      { path: '/products', label: 'Product Items', icon: icon(<Package size={18} strokeWidth={1.75} />) },
    ] : [],
    // Group 2 — purchase / sales items
    hasPermission(Permissions.Products.View) ? [
      { path: '/purchase-items', label: 'Purchase Items', icon: icon(<ShoppingCart size={18} strokeWidth={1.75} />) },
      { path: '/sales-items',    label: 'Sales Items',    icon: icon(<ShoppingBag  size={18} strokeWidth={1.75} />) },
      { path: '/sales-items/print-barcodes', label: t('barcode.bulkPrintMenu'), icon: icon(<Barcode size={18} strokeWidth={1.75} />) },
    ] : [],
  );

  // ──────────────────────────────────────────────────────────────────────────
  // Sales
  // ──────────────────────────────────────────────────────────────────────────
  // Sales-invoice specific items (Invoices, Invoice Analysis, Outstanding Review)
  // are retail-style and don't apply to the restaurant module, which uses its
  // own Order flow instead. Till and Customers stay common to every module.
  // Gate invoice/analysis items on the currently selected APP, not the company's
  // business-vertical module code. A restaurant company can also have the Sales
  // app enabled — in that context invoices SHOULD appear.
  const isRestaurant = currentApp === 'restaurant';
  const salesChildren = section('sales',
    // Group 1 — till open + invoices
    [
      ...(hasPermission(Permissions.Till.Open) ? [
        { path: '/sales/till/open', label: 'Open Till', icon: icon(<Coins size={18} strokeWidth={1.75} />) },
      ] : []),
      ...(!isRestaurant && hasPermission(Permissions.SalesInvoices.View) ? [
        { path: '/sales/sales-invoices', label: 'Invoices', icon: icon(<Receipt size={18} strokeWidth={1.75} />) },
        { path: '/sales/service-items', label: 'Service Items', icon: icon(<Sliders size={18} strokeWidth={1.75} />) },
        { path: '/sales/sales-invoices/inquiry', label: 'Invoice Inquiry', icon: icon(<ScanLine size={18} strokeWidth={1.75} />) },
      ] : []),
    ],
    // Group 2 — analysis + history
    [
      ...(!isRestaurant && hasPermission(Permissions.Reports.View) ? [
        { path: '/reports/sales', label: 'Invoice Analysis', icon: icon(<TrendingUp size={18} strokeWidth={1.75} />) },
      ] : []),
    ],
    // Group 3 — customers + outstanding
    [
      ...(hasPermission(Permissions.Customers.View) ? [
        { path: '/customers', label: 'Customers', icon: icon(<Users size={18} strokeWidth={1.75} />) },
      ] : []),
      ...(!isRestaurant && hasPermission(Permissions.SalesInvoices.View) ? [
        { path: '/sales/sales-invoices/outstanding', label: 'Outstanding Review', icon: icon(<Clock size={18} strokeWidth={1.75} />) },
      ] : []),
    ],
    // Group 4 — till close + history
    [
      ...(hasPermission(Permissions.Till.Close) ? [
        { path: '/sales/till/close', label: 'Close Till', icon: icon(<LockKeyhole size={18} strokeWidth={1.75} />) },
      ] : []),
      ...(hasPermission(Permissions.Till.View) ? [
        { path: '/sales/till/history', label: 'Till History', icon: icon(<History size={18} strokeWidth={1.75} />) },
      ] : []),
      ...(hasPermission(Permissions.DayEnd.View) ? [
        { path: '/sales/day-end', label: 'Day End', icon: icon(<CalendarCheck size={18} strokeWidth={1.75} />) },
      ] : []),
    ],
  );

  // ──────────────────────────────────────────────────────────────────────────
  // Purchase Order
  // ──────────────────────────────────────────────────────────────────────────
  const purchaseOrderChildren = section('purchase-order',
    // Group 1 — suppliers
    hasPermission(Permissions.Suppliers.View) ? [
      { path: '/suppliers', label: 'Suppliers', icon: icon(<Handshake size={18} strokeWidth={1.75} />) },
    ] : [],
    // Group 2 — PO + approvals
    [
      ...(hasPermission(Permissions.PurchaseOrders.View) ? [
        { path: '/purchase/purchase-orders',                label: 'Purchase Order (PO)',      icon: icon(<ClipboardList  size={18} strokeWidth={1.75} />) },
      ] : []),
      ...(hasPermission(Permissions.PurchaseOrders.Approve) ? [
        { path: '/purchase-orders/approvals',              label: 'PO Approval',              icon: icon(<ClipboardCheck size={18} strokeWidth={1.75} />) },
        { path: '/purchase-orders/cancellation-approvals', label: 'PO Approval Cancellation', icon: icon(<Ban            size={18} strokeWidth={1.75} />) },
      ] : []),
    ],
    // Group 3 — PO history
    [
      ...(hasPermission(Permissions.PurchaseOrders.View) ? [
        { path: '/purchase/purchase-orders',  label: 'Purchase Order History', icon: icon(<History size={18} strokeWidth={1.75} />) },
      ] : []),
      ...(hasPermission(Permissions.PurchaseOrders.Approve) ? [
        { path: '/purchase-orders/approvals', label: 'PO Approval History',    icon: icon(<History size={18} strokeWidth={1.75} />) },
      ] : []),
    ],
  );

  // ──────────────────────────────────────────────────────────────────────────
  // Good Received Note (GRN)
  // ──────────────────────────────────────────────────────────────────────────
  const grnChildren = section('grn-section',
    // Group 1 — GRN + approvals + outstanding
    [
      ...(hasPermission(Permissions.GRN.View) ? [
        { path: '/grn',           label: 'Good Received Note (GRN)', icon: icon(<PackageCheck size={18} strokeWidth={1.75} />) },
        { path: '/grn/approvals', label: 'GRN Approvals',            icon: icon(<ShieldCheck  size={18} strokeWidth={1.75} />) },
      ] : []),
      ...(hasPermission(Permissions.PurchaseOrders.View) ? [
        { path: '/purchase-orders/outstanding', label: 'Outstanding Payment', icon: icon(<Clock size={18} strokeWidth={1.75} />) },
      ] : []),
    ],
    // Group 2 — GRN history
    hasPermission(Permissions.GRN.View) ? [
      { path: '/grn',           label: 'GRN History',          icon: icon(<History size={18} strokeWidth={1.75} />) },
      { path: '/grn/approvals', label: 'GRN Approval History', icon: icon(<History size={18} strokeWidth={1.75} />) },
    ] : [],
  );


  // ──────────────────────────────────────────────────────────────────────────
  // Stock Management
  // ──────────────────────────────────────────────────────────────────────────
  const stockChildren = section('stock',
    // Group 1 — levels + movements
    hasPermission(Permissions.Stock.View) ? [
      { path: '/stock/levels',    label: 'Stock Levels',    icon: icon(<Layers   size={18} strokeWidth={1.75} />) },
      { path: '/stock/movements', label: 'Stock Movements', icon: icon(<Activity size={18} strokeWidth={1.75} />) },
    ] : [],
    // Group 2 — reconciliation
    hasPermission(Permissions.Stock.View) ? [
      { path: '/stock-reconciliations', label: 'Stock Reconciliation', icon: icon(<ScanLine size={18} strokeWidth={1.75} />) },
    ] : [],
  );

  // ──────────────────────────────────────────────────────────────────────────
  // Stock Transfer
  // ──────────────────────────────────────────────────────────────────────────
  const stockTransferChildren = section('stock-transfer',
    // Group 1 — out / approval / in
    hasPermission(Permissions.Stock.View) ? [
      { path: '/stock-transfer/out',      label: 'Stock Transfer Out',      icon: icon(<Upload        size={18} strokeWidth={1.75} />) },
      { path: '/stock-transfer/approval', label: 'Stock Transfer Approval', icon: icon(<ShieldCheck   size={18} strokeWidth={1.75} />) },
      { path: '/stock-transfer/in',       label: 'Stock Transfer In',       icon: icon(<Download      size={18} strokeWidth={1.75} />) },
    ] : [],
    // Group 2 — history
    hasPermission(Permissions.Stock.View) ? [
      { path: '/stock-transfer/history',  label: 'Stock Transfer History',  icon: icon(<History       size={18} strokeWidth={1.75} />) },
    ] : [],
  );

  // ──────────────────────────────────────────────────────────────────────────
  // Damage Notes
  // ──────────────────────────────────────────────────────────────────────────
  const damageNoteChildren = section('damage-notes',
    hasPermission(Permissions.DamageNotes.View) ? [
      { path: '/damage-notes',           label: 'Damage Notes',           icon: icon(<AlertTriangle size={18} strokeWidth={1.75} />) },
      { path: '/damage-notes/approvals', label: 'Damage Note Approvals',  icon: icon(<ShieldCheck   size={18} strokeWidth={1.75} />) },
      { path: '/damage-notes/history',   label: 'Damage Note History',    icon: icon(<History       size={18} strokeWidth={1.75} />) },
    ] : [],
  );

  // ──────────────────────────────────────────────────────────────────────────
  // Issue Notes
  // ──────────────────────────────────────────────────────────────────────────
  const issueNoteChildren = section('issue-notes',
    hasPermission(Permissions.IssueNotes.View) ? [
      { path: '/issue-notes',           label: 'Issue Notes',           icon: icon(<ScrollText size={18} strokeWidth={1.75} />) },
      { path: '/issue-notes/approvals', label: 'Issue Note Approvals',  icon: icon(<ShieldCheck size={18} strokeWidth={1.75} />) },
      { path: '/issue-notes/history',   label: 'Issue Note History',    icon: icon(<History    size={18} strokeWidth={1.75} />) },
    ] : [],
  );

  // ──────────────────────────────────────────────────────────────────────────
  // Outlets — shared master used by both Sales and Restaurant, so it is not
  // gated behind the restaurant business-vertical module check below.
  // ──────────────────────────────────────────────────────────────────────────
  const outletsChildren = hasPermission(Permissions.Outlets.View) ? [
    { path: '/restaurant/outlets', label: 'Outlets', icon: icon(<Store size={18} strokeWidth={1.75} />) },
  ] : [];

  // ──────────────────────────────────────────────────────────────────────────
  // Restaurant — only for companies on the restaurant module. The backend
  // enforces this independently ([RequiresModule]); hiding nav is UX only.
  // ──────────────────────────────────────────────────────────────────────────
  const restaurantChildren = !isModule('restaurant') ? [] : section('restaurant',
    hasPermission(Permissions.Orders.View) ? [
      { path: '/restaurant/order',           label: 'Order',           icon: icon(<ShoppingBag     size={18} strokeWidth={1.75} />) },
    ] : [],
    hasPermission(Permissions.Menu.View) ? [
      { path: '/restaurant/menu-types',      label: 'Menu Types',      icon: icon(<Coffee          size={18} strokeWidth={1.75} />) },
      { path: '/restaurant/menu-categories', label: 'Menu Categories', icon: icon(<UtensilsCrossed size={18} strokeWidth={1.75} />) },
      { path: '/restaurant/menu-items',      label: 'Menu Items',      icon: icon(<ChefHat         size={18} strokeWidth={1.75} />) },
      { path: '/restaurant/recipes',         label: 'Recipes',         icon: icon(<FlaskConical    size={18} strokeWidth={1.75} />) },
    ] : [],
    hasPermission(Permissions.Orders.View) ? [
      { path: '/restaurant/orders',    label: 'Order History', icon: icon(<ClipboardList size={18} strokeWidth={1.75} />) },
    ] : [],
    hasPermission(Permissions.Modifiers.View) ? [
      { path: '/restaurant/modifiers', label: 'Modifiers',     icon: icon(<Sliders       size={18} strokeWidth={1.75} />) },
    ] : [],
    hasPermission(Permissions.Menu.View) ? [
      { path: '/restaurant/reports/menu-engineering', label: 'Menu Engineering', icon: icon(<BarChart2 size={18} strokeWidth={1.75} />) },
    ] : [],
    hasPermission(Permissions.HappyHour.View) ? [
      { path: '/restaurant/happy-hours', label: 'Happy Hours', icon: icon(<Clock size={18} strokeWidth={1.75} />) },
    ] : [],
    hasPermission(Permissions.Tables.View) ? [
      { path: '/restaurant/tables',       label: t('table.title', 'Tables'),        icon: icon(<LayoutGrid size={18} strokeWidth={1.75} />) },
      { path: '/restaurant/floor-plan',   label: t('table.floorPlan', 'Floor Plan'),icon: icon(<LayoutGrid size={18} strokeWidth={1.75} />) },
    ] : [],
    hasPermission(Permissions.Reservations.View) ? [
      { path: '/restaurant/reservations', label: t('reservation.title', 'Reservations'), icon: icon(<BookOpen size={18} strokeWidth={1.75} />) },
    ] : [],
    hasPermission(Permissions.KotVoidReasons.Manage) ? [
      { path: '/restaurant/kot-void-reasons', label: 'KOT Void Reasons', icon: icon(<Ban size={18} strokeWidth={1.75} />) },
    ] : [],
  );

  // ──────────────────────────────────────────────────────────────────────────
  // Car Rental — an opt-in add-on app (AppCodes axis), not a business-vertical
  // ModuleCode, so any company can enable it regardless of its own vertical.
  // The backend does not gate these endpoints by module; hiding nav here is
  // UX only, matching the AppRoute("carRental") gate on the routes below.
  // ──────────────────────────────────────────────────────────────────────────
  const carRentalChildren = !availableApps.includes('carRental') ? [] : section('carRental',
    hasPermission(Permissions.RentalBookings.View) ? [
      { path: '/car-rental/schedule', label: 'Schedule',       icon: icon(<CalendarDays  size={18} strokeWidth={1.75} />) },
      { path: '/car-rental/bookings', label: 'Bookings',       icon: icon(<CalendarRange size={18} strokeWidth={1.75} />) },
    ] : [],
    hasPermission(Permissions.Vehicles.View) ? [
      { path: '/car-rental/vehicles', label: 'Vehicles',       icon: icon(<Car size={18} strokeWidth={1.75} />) },
    ] : [],
  );

  // ──────────────────────────────────────────────────────────────────────────
  // AutoMobile — an opt-in add-on app (AppCodes axis), like Car Rental.
  // ──────────────────────────────────────────────────────────────────────────
  const autoMobileChildren = !availableApps.includes('automobile') ? [] : section('automobile',
    hasPermission(Permissions.AutoMobileVehicles.View) ? [
      { path: '/automobile/vehicles', label: 'Vehicles', icon: icon(<Car size={18} strokeWidth={1.75} />) },
    ] : [],
    hasPermission(Permissions.AutoMobileInvoices.View) ? [
      { path: '/automobile/invoices', label: 'Invoices', icon: icon(<Receipt size={18} strokeWidth={1.75} />) },
    ] : [],
  );

  // ──────────────────────────────────────────────────────────────────────────
  // Accounting — an opt-in add-on app (AppCodes axis), available to every
  // business vertical: General Ledger, Chart of Accounts, AR, AP.
  // ──────────────────────────────────────────────────────────────────────────
  const accountingChildren = !availableApps.includes('accounting') ? [] : section('accounting',
    hasPermission(Permissions.ChartOfAccounts.View) ? [
      { path: '/accounting/chart-of-accounts', label: 'Chart of Accounts', icon: icon(<ListTree size={18} strokeWidth={1.75} />) },
    ] : [],
    hasPermission(Permissions.GeneralLedger.View) ? [
      { path: '/accounting/journal-entries', label: 'Journal Entries', icon: icon(<BookOpen size={18} strokeWidth={1.75} />) },
      { path: '/accounting/trial-balance',   label: 'Trial Balance',   icon: icon(<Calculator size={18} strokeWidth={1.75} />) },
    ] : [],
    hasPermission(Permissions.AccountsReceivable.View) ? [
      { path: '/accounting/accounts-receivable', label: 'Accounts Receivable', icon: icon(<Landmark size={18} strokeWidth={1.75} />) },
    ] : [],
    hasPermission(Permissions.AccountsPayable.View) ? [
      { path: '/accounting/accounts-payable', label: 'Accounts Payable', icon: icon(<Wallet size={18} strokeWidth={1.75} />) },
    ] : [],
  );

  // HR & Attendance — a standalone app rather than a business vertical, so it is
  // gated on the app entitlement only and not on the company's ModuleCode.
  const hrChildren = !availableApps.includes('hr') ? [] : section('hr',
    hasPermission(Permissions.Employees.View) ? [
      { path: '/hr/employees', label: t('employee.menu'), icon: icon(<Users size={18} strokeWidth={1.75} />) },
    ] : [],
    hasPermission(Permissions.AttendanceRecords.View) ? [
      { path: '/hr/attendance', label: t('attendance.menu'), icon: icon(<CalendarCheck size={18} strokeWidth={1.75} />) },
    ] : [],
    hasPermission(Permissions.LeaveRequests.View) ? [
      { path: '/hr/leave-requests', label: t('leaveRequest.menu'), icon: icon(<CalendarDays size={18} strokeWidth={1.75} />) },
    ] : [],
  );

  // ──────────────────────────────────────────────────────────────────────────
  // Reports  (3-level: Reports > Sales/Purchase/Stock > individual pages)
  // ──────────────────────────────────────────────────────────────────────────
  const reportsChildren: MenuItem[] = [];

  // Which Reports sub-groups apply to each app. Sales & Restaurant only need
  // Sales reports; Inventory only needs Purchase + Inventory reports. Platform
  // (admin) and the not-yet-resolved state see everything.
  const showSalesReports      = !currentApp || currentApp === 'platform' || currentApp === 'sales' || currentApp === 'restaurant';
  const showPurchaseReports   = !currentApp || currentApp === 'platform' || currentApp === 'inventory';
  const showInventoryReports  = !currentApp || currentApp === 'platform' || currentApp === 'inventory';

  if (hasPermission(Permissions.Reports.View)) {
    // Sales sub-group
    if (showSalesReports) {
      reportsChildren.push({
        path: '/reports/sales',
        label: 'Sales',
        icon: icon(<ShoppingBag size={18} strokeWidth={1.75} />),
        children: [
          { path: '/reports/sales/summary',     label: 'Sales Summary',     icon: icon(<FileBarChart size={18} strokeWidth={1.75} />) },
          { path: '/reports/sales/by-product',  label: 'Sales by Product',  icon: icon(<Package      size={18} strokeWidth={1.75} />) },
          { path: '/reports/sales/by-customer', label: 'Sales by Customer', icon: icon(<Users        size={18} strokeWidth={1.75} />) },
          { path: '/reports/sales/trends',      label: 'Sales Trends',      icon: icon(<TrendingUp   size={18} strokeWidth={1.75} />) },
        ],
      });
    }
    // Purchase sub-group
    if (showPurchaseReports) {
      reportsChildren.push({
        path: '/reports/purchase',
        label: 'Purchase',
        icon: icon(<ShoppingCart size={18} strokeWidth={1.75} />),
        children: [
          { path: '/reports', label: 'Purchase Order Reports', icon: icon(<ClipboardList size={18} strokeWidth={1.75} />) },
          { path: '/reports', label: 'GRN Reports',            icon: icon(<PackageCheck  size={18} strokeWidth={1.75} />) },
        ],
      });
    }
    // Inventory / Stock sub-group
    if (showInventoryReports) {
      reportsChildren.push({
        path: '/reports/inventory',
        label: 'Inventory',
        icon: icon(<Layers size={18} strokeWidth={1.75} />),
        children: [
          { path: '/reports/inventory/stock-summary',   label: 'Stock Summary',        icon: icon(<Package        size={18} strokeWidth={1.75} />) },
          { path: '/reports/inventory/low-stock',       label: 'Low Stock Alert',       icon: icon(<AlertTriangle  size={18} strokeWidth={1.75} />) },
          { path: '/reports/inventory/dead-stock',      label: 'Dead Stock',            icon: icon(<ScanLine       size={18} strokeWidth={1.75} />) },
          { path: '/reports/inventory/valuation',       label: 'Stock Valuation',       icon: icon(<DollarSign     size={18} strokeWidth={1.75} />) },
          { path: '/reports/inventory/movements',       label: 'Movement Ledger',       icon: icon(<Activity       size={18} strokeWidth={1.75} />) },
          { path: '/reports/inventory/grn',             label: 'GRN Report',            icon: icon(<PackageCheck   size={18} strokeWidth={1.75} />) },
          { path: '/reports/inventory/adjustments',     label: 'Stock Adjustments',     icon: icon(<ArrowLeftRight size={18} strokeWidth={1.75} />) },
          { path: '/reports/inventory/opening-closing', label: 'Opening / Closing',     icon: icon(<CalendarRange  size={18} strokeWidth={1.75} />) },
        ],
      });
    }
  }

  // ──────────────────────────────────────────────────────────────────────────
  // Configurations  (no permission on the group — show if any child visible)
  // ──────────────────────────────────────────────────────────────────────────
  const configChildren: MenuItem[] = [];

  // — Organization sub-group —
  const orgSubItems: MenuItem[] = [
    ...(hasPermission(Permissions.Settings.View) ? [
      { path: '/settings/company', label: 'Company Settings', icon: icon(<Building2 size={18} strokeWidth={1.75} />) },
    ] : []),
    ...(hasPermission(Permissions.Warehouses.View) ? [
      { path: '/organization/warehouses', label: 'Warehouses', icon: icon(<Warehouse size={18} strokeWidth={1.75} />) },
    ] : []),
    ...(hasPermission(Permissions.Departments.View) ? [
      { path: '/organization/departments', label: 'Departments', icon: icon(<Building2 size={18} strokeWidth={1.75} />) },
    ] : []),
    ...(hasAnyPermission([Permissions.Taxes.View, Permissions.Taxes.Create, Permissions.Taxes.Edit, Permissions.Taxes.Delete]) ? [
      { path: '/products/taxes', label: 'Taxes', icon: icon(<DollarSign size={18} strokeWidth={1.75} />) },
    ] : []),
    ...(hasPermission(Permissions.Settings.View) ? [
      { path: '/settings/approval-rules', label: 'Approval Rules', icon: icon(<ClipboardCheck size={18} strokeWidth={1.75} />) },
    ] : []),
    ...(hasAnyPermission([
      Permissions.Products.Create, Permissions.Suppliers.Create, Permissions.Customers.Create,
      Permissions.PurchaseOrders.Create, Permissions.SalesInvoices.Create, Permissions.Menu.Create,
    ]) ? [
      { path: '/settings/data-import', label: t('dataImport.menu'), icon: icon(<Upload size={18} strokeWidth={1.75} />) },
    ] : []),
  ];
  if (hasItems(orgSubItems)) {
    configChildren.push({
      path: '/configurations/organization',
      label: 'Organization',
      icon: icon(<Building2 size={18} strokeWidth={1.75} />),
      children: orgSubItems,
    });
  }

  // — Sales Settings sub-group —
  const salesSettingsItems: MenuItem[] = [
    ...(hasPermission(Permissions.PaymentTypes.View) ? [
      { path: '/settings/payment-types', label: 'Payment Types', icon: icon(<CreditCard size={18} strokeWidth={1.75} />) },
    ] : []),
    ...(hasPermission(Permissions.Coupons.View) ? [
      { path: '/sales/coupons', label: t('coupon.menu'), icon: icon(<TicketPercent size={18} strokeWidth={1.75} />) },
    ] : []),
    ...(hasPermission(Permissions.Promotions.View) ? [
      { path: '/sales/promotions', label: t('promotion.menu'), icon: icon(<BadgePercent size={18} strokeWidth={1.75} />) },
    ] : []),
    ...(hasPermission(Permissions.ProductBundles.View) ? [
      { path: '/sales/product-bundles', label: t('productBundle.menu'), icon: icon(<Layers size={18} strokeWidth={1.75} />) },
    ] : []),
    ...(hasPermission(Permissions.Settings.Edit) ? [
      { path: '/settings/bill-printing', label: t('billPrint.menu'), icon: icon(<Receipt size={18} strokeWidth={1.75} />) },
      { path: '/settings/printing-devices', label: t('printDevices.menu'), icon: icon(<Printer size={18} strokeWidth={1.75} />) },
    ] : []),
  ];
  if (hasItems(salesSettingsItems)) {
    configChildren.push({
      path: '/configurations/sales-settings',
      label: 'Sales Settings',
      icon: icon(<Store size={18} strokeWidth={1.75} />),
      children: salesSettingsItems,
    });
  }

  // — Product Settings sub-group —
  const productSettingsItems: MenuItem[] = hasPermission(Permissions.Products.View) ? [
    { path: '/products/categories',     label: t('product.category'), icon: icon(<Tag          size={18} strokeWidth={1.75} />) },
    { path: '/products/units',          label: t('product.units'),             icon: icon(<Ruler        size={18} strokeWidth={1.75} />) },
    { path: '/products/unit-conversions',label: t('product.unitConversions'), icon: icon(<ArrowLeftRight size={18} strokeWidth={1.75} />) },
    { path: '/products/attributes',     label: t('attribute.title'),  icon: icon(<Tag          size={18} strokeWidth={1.75} />) },
    ...(hasPermission(Permissions.Products.Edit) ? [
      { path: '/products/barcode-settings', label: t('barcode.settingsMenu'), icon: icon(<Barcode size={18} strokeWidth={1.75} />) },
    ] : []),
  ] : [];
  if (hasItems(productSettingsItems)) {
    configChildren.push({
      path: '/configurations/product-settings',
      label: t('menu.product-configuration'),
      icon: icon(<Package size={18} strokeWidth={1.75} />),
      children: productSettingsItems,
    });
  }

  // — User Settings sub-group —
  const userSettingsItems: MenuItem[] = [
    ...(hasPermission(Permissions.Roles.View) ? [
      { path: '/roles', label: t('role.title'), icon: icon(<KeyRound size={18} strokeWidth={1.75} />) },
    ] : []),
    ...(hasPermission(Permissions.Users.View) ? [
      { path: '/users', label: t('user.title'), icon: icon(<User size={18} strokeWidth={1.75} />) },
    ] : []),
  ];
  if (hasItems(userSettingsItems)) {
    configChildren.push({
      path: '/configurations/user-settings',
      label: t('menu.user-settings'),
      icon: icon(<Users size={18} strokeWidth={1.75} />),
      children: userSettingsItems,
    });
  }

  // ──────────────────────────────────────────────────────────────────────────
  // Final navigation items
  // ──────────────────────────────────────────────────────────────────────────
  const allNavigationItems: MenuItem[] = [
    // Always visible
    { path: '/dashboard', label: 'Inventory Dashboard', icon: icon(<LayoutDashboard size={18} strokeWidth={1.75} />), apps: ['inventory'] },

    // Sales Dashboard
    ...(hasPermission(Permissions.SalesInvoices.View) ? [
      { path: '/sales/dashboard', label: 'Sales Dashboard', icon: icon(<BarChart2 size={18} strokeWidth={1.75} />), apps: ['sales', 'restaurant'] },
    ] : []),

    // Products
    ...(hasItems(productsChildren) ? [{
      path: '/products',
      label: 'Products',
      icon: icon(<Package size={18} strokeWidth={1.75} />),
      children: productsChildren,
      apps: ['inventory'],
    }] : []),

    // Sales — for the Restaurant app, salesChildren is already trimmed down to
    // the till process (isRestaurant strips Invoices/Analysis/Outstanding above).
    ...(hasItems(salesChildren) ? [{
      path: '/sales',
      label: 'Sales',
      icon: icon(<ShoppingBag size={18} strokeWidth={1.75} />),
      children: salesChildren,
      apps: ['sales', 'restaurant'],
    }] : []),

    // Outlets — shared master used by both Sales and Restaurant
    ...outletsChildren.map((item) => ({ ...item, apps: ['sales', 'restaurant'] })),

    // Restaurant
    ...(hasItems(restaurantChildren) ? [{
      path: '/restaurant',
      label: 'Restaurant',
      icon: icon(<UtensilsCrossed size={18} strokeWidth={1.75} />),
      children: restaurantChildren,
      apps: ['restaurant'],
    }] : []),

    // Car Rental
    ...(hasItems(carRentalChildren) ? [{
      path: '/car-rental',
      label: 'Car Rental',
      icon: icon(<Car size={18} strokeWidth={1.75} />),
      children: carRentalChildren,
      apps: ['carRental'],
    }] : []),

    // Customers — also available in the Car Rental app
    ...(hasPermission(Permissions.Customers.View) ? [
      { path: '/customers', label: 'Customers', icon: icon(<Users size={18} strokeWidth={1.75} />), apps: ['carRental'] },
    ] : []),

    // AutoMobile
    ...(hasItems(autoMobileChildren) ? [{
      path: '/automobile',
      label: 'AutoMobile',
      icon: icon(<Car size={18} strokeWidth={1.75} />),
      children: autoMobileChildren,
      apps: ['automobile'],
    }] : []),

    // HR & Attendance — standalone app, available regardless of the company's vertical
    ...(hasItems(hrChildren) ? [{
      path: '/hr',
      label: t('hr.menu'),
      icon: icon(<Users size={18} strokeWidth={1.75} />),
      children: hrChildren,
      apps: ['hr'],
    }] : []),

    // Accounting
    ...(hasItems(accountingChildren) ? [{
      path: '/accounting',
      label: 'Accounting',
      icon: icon(<Landmark size={18} strokeWidth={1.75} />),
      children: accountingChildren,
      apps: ['accounting'],
    }] : []),

    // Purchase Order
    ...(hasItems(purchaseOrderChildren) ? [{
      path: '/purchase',
      label: 'Purchase Order',
      icon: icon(<ClipboardList size={18} strokeWidth={1.75} />),
      children: purchaseOrderChildren,
      apps: ['inventory'],
    }] : []),

    // Good Received Note (GRN)
    ...(hasItems(grnChildren) ? [{
      path: '/grn',
      label: 'Good Received Note (GRN)',
      icon: icon(<PackageCheck size={18} strokeWidth={1.75} />),
      children: grnChildren,
      apps: ['inventory'],
    }] : []),

    // Stock Transfer
    ...(hasItems(stockTransferChildren) ? [{
      path: '/stock-transfer',
      label: 'Stock Transfer',
      icon: icon(<ArrowLeftRight size={18} strokeWidth={1.75} />),
      children: stockTransferChildren,
      apps: ['inventory'],
    }] : []),

    // Issue Notes
    ...(hasItems(issueNoteChildren) ? [{
      path: '/issue-notes',
      label: 'Issue Notes',
      icon: icon(<ScrollText size={18} strokeWidth={1.75} />),
      children: issueNoteChildren,
      apps: ['inventory'],
    }] : []),

    // Damage Notes
    ...(hasItems(damageNoteChildren) ? [{
      path: '/damage-notes',
      label: 'Damage Notes',
      icon: icon(<AlertTriangle size={18} strokeWidth={1.75} />),
      children: damageNoteChildren,
      apps: ['inventory'],
    }] : []),

    // Stock Management
    ...(hasItems(stockChildren) ? [{
      path: '/stock',
      label: 'Stock Management',
      icon: icon(<Layers size={18} strokeWidth={1.75} />),
      children: stockChildren,
      apps: ['inventory'],
    }] : []),

    // Reports — cross-cutting, shown under every app
    ...(hasItems(reportsChildren) ? [{
      path: '/reports',
      label: 'Reports',
      icon: icon(<FileBarChart size={18} strokeWidth={1.75} />),
      children: reportsChildren,
    }] : []),

    // Configurations (no permission on parent — visible if any child is)
    ...(hasItems(configChildren) ? [{
      path: '/configurations',
      label: 'Configurations',
      icon: icon(<Settings size={18} strokeWidth={1.75} />),
      children: configChildren,
      apps: ['platform'],
    }] : []),
  ];

  // Filter top-level sections by the currently selected app (model). Items
  // without an `apps` tag (e.g. Reports) are cross-cutting and always shown.
  // While currentApp hasn't resolved yet (still loading), show everything.
  const navigationItems: MenuItem[] = currentApp
    ? allNavigationItems.filter((item) => !item.apps || item.apps.includes(currentApp))
    : allNavigationItems;

  // ── Cashier pass-through — no sidebar, just a slim back-to-POS header ───
  if (isCashier) {
    return (
      <div className="cashier-passthrough-shell">
        <div className="cashier-passthrough-bar">
          <button
            className="cashier-passthrough-back"
            onClick={() => navigate('/cashier/order')}
          >
            ← {t('cashier.backToPOS', 'Back to POS')}
          </button>
        </div>
        <div className="cashier-passthrough-content">
          {children}
        </div>
      </div>
    );
  }

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <div className="layout-container">
      <Sidebar
        navigationItems={navigationItems}
        isExpanded={sidebarExpanded}
        onToggle={setSidebarExpanded}
      />
      <div className={`layout-content ${sidebarExpanded ? 'sidebar-expanded' : 'sidebar-collapsed'}`}>
        <header className="layout-header">

          {/* ── Brand ──────────────────────────────────────────────────── */}
          <div className="layout-header-brand">
            <img
              src={ProductLog}
              alt={APPLICATION_SHORT_NAME}
              className="layout-header-logo"
            />
              <span className="layout-header-title-full">{APPLICATION_NAME}</span>
              <span className="layout-header-title-short">{APPLICATION_SHORT_NAME}</span>
          </div>

          {/* ── Right controls ───────────────────────────────────────── */}
          <div className="layout-header-right">
            <div className="layout-header-tools">
              <AppSwitcher />
              <TillStatusPill />
              <NotificationBell />
              <ThemeToggle />
            </div>

            <div className="layout-header-divider" aria-hidden="true" />

            {/* User identity */}
            <button
              className="layout-header-user"
              onClick={() => navigate('/settings/profile')}
              title={t('navigation.myProfile')}
              aria-label={t('navigation.myProfile')}
            >
              <div className="avatar avatar-sm avatar-primary" aria-hidden="true">
                {user?.firstName?.[0]}{user?.lastName?.[0]}
              </div>
              <div className="layout-header-user-text">
                <span className="layout-header-user-name">
                  {user?.firstName} {user?.lastName}
                </span>
                <span className="layout-header-user-role">
                  {user?.roles?.map(r => r.name).join(', ') || 'User'}
                </span>
              </div>
            </button>

            <div className="layout-header-divider" aria-hidden="true" />

            {/* Logout */}
            <button
              className="layout-logout-btn"
              onClick={handleLogout}
              title={t('navigation.logout')}
              aria-label={t('navigation.logout')}
            >
              <LogOut size={17} strokeWidth={2} aria-hidden={true} />
              <span className="layout-logout-label">{t('navigation.logout')}</span>
            </button>
          </div>
        </header>
        <main>{children}</main>
      </div>
    </div>
  );
};

export default Layout;
